import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle, BadgeCheck, BarChart2, FileText, Mail, Receipt, ScrollText, ShieldAlert,
} from 'lucide-react';
import { ADMIN_PALETTE } from './adminDesignSystem';
import { API, StatCard } from './compliance/complianceShared';
import { InvoicesTab } from './compliance/InvoicesTab';
import { DAC7Tab } from './compliance/DAC7Tab';
import { VatGapsTab } from './compliance/VatGapsTab';
import { UboTab } from './compliance/UboTab';
import { MyDataControlTab } from './compliance/MyDataControlTab';

const BASE_TABS = [
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'dac7', label: 'DAC7 tracker', icon: BarChart2 },
  { id: 'vat-gaps', label: 'VAT gaps', icon: AlertTriangle },
  { id: 'ubo', label: 'UBO', icon: BadgeCheck },
];

const MAIN_ADMIN_TAB = { id: 'mydata', label: 'myDATA control', icon: ShieldAlert };

export function AdminCompliancePanel({ headers, currentUser }) {
  const isMainAdmin = Boolean(currentUser?.is_main_admin);
  const TABS = useMemo(
    () => (isMainAdmin ? [...BASE_TABS, MAIN_ADMIN_TAB] : BASE_TABS),
    [isMainAdmin],
  );
  const [activeTab, setActiveTab] = useState('invoices');
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/compliance/overview`, { headers });
        setOverview(res.data);
      } catch (e) {
        // silent — overview is nice-to-have
      }
    })();
  }, [headers, activeTab]);

  return (
    <div data-testid="admin-compliance-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <ScrollText size={18} color={ADMIN_PALETTE.brandRed} />
        <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: ADMIN_PALETTE.ink }}>
          Legal &amp; Compliance
        </h2>
      </div>

      {overview && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <StatCard icon={FileText} label={`Invoices in ${overview.fiscal_year}`} value={overview.invoices_this_year} tone="info" testid="overview-stat-year" />
          <StatCard icon={AlertTriangle} label="Emails failed" value={overview.emails_failed} tone={overview.emails_failed ? 'danger' : 'ok'} testid="overview-stat-failed" />
          <StatCard icon={Mail} label="Emails pending" value={overview.emails_pending} tone={overview.emails_pending ? 'warn' : 'ok'} testid="overview-stat-pending" />
          <StatCard
            icon={AlertTriangle}
            label="VAT gaps"
            value={(overview.vat_gaps?.venues || 0) + (overview.vat_gaps?.hosts || 0)}
            tone={((overview.vat_gaps?.venues || 0) + (overview.vat_gaps?.hosts || 0)) ? 'warn' : 'ok'}
            testid="overview-stat-vat"
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              data-testid={`compliance-tab-${t.id}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 999,
                border: `1px solid ${active ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border}`,
                background: active ? ADMIN_PALETTE.ink : ADMIN_PALETTE.surface,
                color: active ? '#FFFFFF' : ADMIN_PALETTE.ink,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'invoices' && <InvoicesTab headers={headers} />}
      {activeTab === 'dac7' && <DAC7Tab headers={headers} />}
      {activeTab === 'vat-gaps' && <VatGapsTab headers={headers} />}
      {activeTab === 'ubo' && <UboTab headers={headers} />}
      {activeTab === 'mydata' && isMainAdmin && <MyDataControlTab headers={headers} />}
    </div>
  );
}
